import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}


export async function retry<T>(fn: () => Promise<T>, errorType?: Error, retries = 3, delay = 100): Promise<T> {
  console.log("retry", retries);
  return fn().catch(async (error) => {
    if (retries > 0 && errorType && error === errorType) {
      return new Promise((resolve) => setTimeout(resolve, delay))
        .then(() => retry(fn, errorType, retries - 1, delay * 2));
    }
    throw error;
  });
}

/**
 * Normalizes a string by removing accents, converting to lowercase
 * Useful for case and accent insensitive string comparisons
 * 
 * @example
 * normalizeString("José María") // "jose maria"
 * normalizeString("Ñoño") // "nono"
 */
export function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Converts a free-form header string into a safe snake_case identifier
 * usable as `fieldName`. Strips accents, replaces non-alphanumeric
 * characters with underscores, and ensures the identifier does not start
 * with a digit. Falls back to `field` for empty results.
 *
 * @example
 * sanitizeFieldName("Número de póliza") // "numero_de_poliza"
 * sanitizeFieldName("First Name!!") // "first_name"
 * sanitizeFieldName("123abc") // "_123abc"
 */
export function sanitizeFieldName(header: string): string {
  const cleaned = normalizeString(header)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!cleaned) {
    return 'field';
  }
  if (/^[0-9]/.test(cleaned)) {
    return `_${cleaned}`;
  }
  return cleaned;
}

/**
 * Produces a unique `fieldName` from a header, appending a numeric suffix
 * when the sanitized base already exists in `existing`. Mutates `existing`
 * by adding the chosen name so it can be reused for the next call.
 */
export function generateUniqueFieldName(header: string, existing: Set<string>): string {
  const base = sanitizeFieldName(header);
  if (!existing.has(base)) {
    existing.add(base);
    return base;
  }
  let suffix = 2;
  let candidate = `${base}_${suffix}`;
  while (existing.has(candidate)) {
    suffix += 1;
    candidate = `${base}_${suffix}`;
  }
  existing.add(candidate);
  return candidate;
}

export function spellOutCharacters(text: string, locale: 'es' | 'en' | 'ca'): string {
  const digitMaps = {
    es: {
      '0': 'cero',
      '1': 'uno',
      '2': 'dos',
      '3': 'tres',
      '4': 'cuatro',
      '5': 'cinco',
      '6': 'seis',
      '7': 'siete',
      '8': 'ocho',
      '9': 'nueve',
    },
    en: {
      '0': 'zero',
      '1': 'one',
      '2': 'two',
      '3': 'three',
      '4': 'four',
      '5': 'five',
      '6': 'six',
      '7': 'seven',
      '8': 'eight',
      '9': 'nine',
    },
    ca: {
      '0': 'zero',
      '1': 'u',
      '2': 'dos',
      '3': 'tres',
      '4': 'quatre',
      '5': 'cinc',
      '6': 'sis',
      '7': 'set',
      '8': 'vuit',
      '9': 'nou',
    },
  };

  const digitMap = digitMaps[locale];

  return text
    .split('')
    .map(char => {
      // Handle digits
      if (char in digitMap) {
        return digitMap[char as keyof typeof digitMap];
      }
      
      // Handle 'V' or 'v' specifically
      if (char === 'V' || char === 'v') {
        return locale === 'es' || locale === 'ca' ? 'uve' : 'vee';
      }
      
      // Keep other letters as they are (lowercase)
      return char.toLowerCase();
    })
    .join(' ');
}

